
import React from 'react';
import { Button } from '@/components/ui/button';
import { Notification } from '@/types/doctor';
import { MessageSquare, Calendar, BellRing, FileText } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

interface NotificationListProps {
  notifications: Notification[];
  onMarkRead?: (notificationId: string) => void;
}

const NotificationList: React.FC<NotificationListProps> = ({ 
  notifications, 
  onMarkRead 
}) => {
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'message':
        return <MessageSquare className="h-4 w-4 text-blue-500" />;
      case 'appointment':
        return <Calendar className="h-4 w-4 text-purple-500" />;
      case 'plan':
        return <FileText className="h-4 w-4 text-green-500" />;
      case 'system':
      default:
        return <BellRing className="h-4 w-4 text-orange-500" />;
    }
  };

  const formatNotificationTime = (dateString: string) => {
    const date = new Date(dateString);
    // If less than 24 hours ago, show as 'X hours ago'
    if (Date.now() - date.getTime() < 24 * 60 * 60 * 1000) {
      return formatDistanceToNow(date, { addSuffix: true });
    }
    // Otherwise show the actual date
    return format(date, 'MMM d, yyyy');
  };

  if (notifications.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground">
        No new notifications
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {notifications.map((notification) => (
        <div 
          key={notification.id} 
          className={`p-3 rounded-lg border ${
            notification.isRead || notification.read ? 'bg-background' : 'bg-muted/30'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              {getNotificationIcon(notification.type)}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm">{notification.title}</h4>
              <p className="text-sm text-muted-foreground line-clamp-2">
                {notification.description || notification.message}
              </p>
              <span className="text-xs text-muted-foreground mt-1 block">
                {formatNotificationTime(notification.date || notification.created_at || '')}
              </span>
            </div>
            {!(notification.isRead || notification.read) && onMarkRead && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="self-start"
                onClick={() => onMarkRead(notification.id)}
              >
                Mark read
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default NotificationList;
